/**
 * SidebarItems.tsx — componenti presentazionali della sidebar.
 *
 * Estratti da Navbar.tsx (28 Ott 2026) per ridurre il monolite e poterli
 * riusare/testare senza tirare dentro WebSocket e store di notifiche.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import useIsActive from "@/components/nav/useIsActive";

export function SidebarLink({
  to,
  icon,
  label,
  badge,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  badge?: number;
}) {
  const isActive = useIsActive();
  const active = isActive(to);
  return (
    <Link
      to={to}
      className={`flex flex-row items-center gap-3 p-2 rounded-lg transition-colors ${
        active
          ? "text-brand-500"
          : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
      }`}
    >
      <div className="relative">
        {icon}
        {badge && badge > 0 ? (
          <span className="absolute -top-2 -right-2 min-w-[16px] h-[16px] px-0.5 rounded-full bg-orange-500 text-black text-[10px] font-extrabold flex items-center justify-center">
            {badge > 99 ? "99+" : badge}
          </span>
        ) : null}
      </div>
      <span className="text-sm font-medium">{label}</span>
    </Link>
  );
}

/* SidebarLinkBadge — variante con badge personalizzato (colore custom) */
export function SidebarLinkBadge({
  to,
  icon,
  label,
  badge,
  badgeColor = "bg-orange-500",
}: {
  to: string;
  icon: ReactNode;
  label: string;
  badge?: number;
  badgeColor?: string;
}) {
  const isActive = useIsActive();
  const active = isActive(to);
  return (
    <Link
      to={to}
      className={`flex flex-row items-center gap-3 p-2 rounded-lg transition-colors ${
        active
          ? "text-brand-500"
          : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
      }`}
    >
      <div className="relative">
        {icon}
        {badge && badge > 0 ? (
          <span
            className={`absolute -top-2 -right-2 min-w-[16px] h-[16px] px-0.5 rounded-full ${badgeColor} text-white text-[10px] font-extrabold flex items-center justify-center`}
          >
            {badge > 99 ? "99+" : badge}
          </span>
        ) : null}
      </div>
      <span className="text-sm font-medium">{label}</span>
    </Link>
  );
}

/* SectionHeader — titolo sezione nella sidebar (Dealer / Chapter Admin) */
export function SectionHeader({
  label,
  color = "neutral",
}: {
  label: string;
  color?: "blue" | "purple" | "orange" | "green" | "neutral";
}) {
  const colors: Record<string, string> = {
    blue: "text-blue-400",
    purple: "text-purple-400",
    orange: "text-orange-400",
    green: "text-green-400",
    neutral: "text-neutral-500",
  };
  return (
    <div className="mt-4 mb-1 first:mt-0 px-2">
      <span
        className={`text-[10px] font-bold uppercase tracking-widest ${colors[color]}`}
      >
        {label}
      </span>
    </div>
  );
}
